import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, UserRound, Phone, HeartPulse } from "lucide-react";
import PatientCustomFields from "./PatientCustomFields";
import PatientProfileHistory from "./PatientProfileHistory";
import api from "../../api";
import { languageName } from "../PatientLanguageFields";

// Profile tab on the patient file: everything recorded on the patient, with a
// pencil per panel. Background/history, custom fields and the change history only for
// people who can see clinical notes.
const show = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (Array.isArray(v)) return v.length ? v.join(", ") : null;
  if (typeof v === "object") return Object.entries(v).filter(([, x]) => x).map(([k, x]) => `${k.replace(/_/g, " ")}: ${x}`).join(" · ") || null;
  if (typeof v === "string" && v.trim().startsWith("{")) { try { return show(JSON.parse(v)); } catch { return v; } }
  return String(v);
};

// A row is [label, value] or [label, value, href] (e.g. tel: for tap-to-call).
function Section({ icon: Icon, title, rows, oneColumn = false, className = "", onEdit }) {
  const filled = rows.filter(([, v]) => show(v));
  return (
    <div className={`rounded-xl bg-white border border-secondary-100 p-4 ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="flex items-center gap-2 text-sm font-bold text-secondary-900"><Icon className="w-4 h-4 text-primary-600" /> {title}</p>
        {onEdit && (
          <button type="button" onClick={onEdit} title={`Edit ${title.toLowerCase()}`} aria-label={`Edit ${title.toLowerCase()}`}
            className="p-1.5 rounded-lg text-secondary-400 hover:text-primary-700 hover:bg-primary-50">
            <Pencil className="w-4 h-4" />
          </button>
        )}
      </div>
      {filled.length === 0 ? <p className="text-sm text-secondary-400">Nothing recorded yet.</p> : (
        <dl className={`grid grid-cols-1 ${oneColumn ? "" : "sm:grid-cols-2"} gap-x-6 gap-y-2.5`}>
          {filled.map(([label, v, href]) => (
            <div key={label} className="min-w-0">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-secondary-400">{label}</dt>
              <dd className="text-sm text-secondary-800 break-words whitespace-pre-line">
                {href ? <a href={href} className="font-semibold text-primary-700 hover:underline">{show(v)}</a> : show(v)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export default function PatientProfilePanel({ patientId, canViewClinical }) {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.getPatient(patientId).then((r) => setP(r.data)).catch((e) => setError(e?.response?.data?.error || "Couldn't load the profile."));
  }, [patientId]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!p) return <p className="text-sm text-secondary-500">Loading…</p>;
  const edit = (section) => () => navigate(`/patient-edit/#${section}`, { state: p });
  const age = p.dob ? Math.floor((Date.now() - new Date(`${p.dob}T00:00:00`)) / 31557600000) : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
      <Section icon={UserRound} title="Personal details" className="lg:col-span-2" onEdit={edit("personal")} rows={[
        ["Name", p.name], ["Age", age != null ? `${age} yrs${p.dob_is_estimated ? " (approx.)" : ""}` : null], ["Date of birth", p.dob_is_estimated ? null : p.dob],
        ["Gender", p.gender], ["Pronouns", p.pronouns], ["Phone", p.contact && !String(p.contact).startsWith("auto_") ? p.contact : null], ["Email", p.email], ["Address", p.address],
        ["Occupation", p.occupation], ["Relationship status", p.relationshipstatus],
        ["Communication language", p.language ? languageName(p.language) : null], ["Languages understood", p.languages_known],
        ["Consultation type", p.consultationtype],
      ]} />
      <Section icon={Phone} title="Emergency contact" oneColumn onEdit={edit("emergency")} rows={[
        ["Name", p.emergencyname], ["Relationship", p.emergencyrelation],
        ["Phone", p.emergencyphone, p.emergencyphone ? `tel:${String(p.emergencyphone).replace(/[^\d+]/g, "")}` : null],
      ]} />
      </div>
      {canViewClinical && (
        <Section icon={HeartPulse} title="Background & history" onEdit={edit("history")} rows={[
          ["Presenting concerns", p.concerns], ["For how long", p.duration], ["Symptoms", p.symptoms], ["Psychiatric history", p.psychiatrichistory],
          ["Family history", p.familyhistory], ["Substance use", p.substanceuse], ["Medical conditions", p.conditions], ["Medications (as the patient reports)", p.medications],
          ["Allergies", p.allergies], ["Previous therapy or counselling", p.therapyhistory], ["Support system", p.supportsystem], ["Lifestyle", p.lifestyle],
        ]} />
      )}
      {canViewClinical && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <PatientCustomFields patientId={patientId} />
          <PatientProfileHistory patientId={patientId} />
        </div>
      )}
    </div>
  );
}
